import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6 } from "styled-components";
import type { Mode } from "~/features/ui/get-theme-style";
import { takeFirst } from "~/features/app/utils";
import { Button, type ButtonVariant } from "~/features/ui/button";
import { Link } from "~/features/ui/link";
import { Wrap } from "~/features/ui/wrap";
import { Grid } from "~/features/ui/grid";
import { Headline } from "~/features/ui/headline";
import { RichText } from "~/features/ui/rich-text";
import { Eyebrow } from "~/features/ui/eyebrow";
import { isLinkValid } from "~/features/ui/link/utils";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
type PlainLink = Record<string, unknown> & {
  href?: string;
  newWindow?: boolean;
  text?: React.ReactNode;
};
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
function getPlainLinkIcon(link?: PlainLink | null) {
  if (!link) {
    return undefined;
  }
  return link.newWindow ? ArrowTopRight : ArrowRight;
}
export type HeadlineBlockViewProps = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  eyebrow?: string | null;
  hasSolidBackground?: boolean | null;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  highlight?: boolean | null;
  isHighlighted?: boolean | null;
  link?: PlainLink | Array<PlainLink | null | undefined> | null;
  linkVariant?: string | null;
  mode?: string | null;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  text?: React.ReactNode;
  theme?: string | null;
};
export function HeadlineBlockView({
  anchor,
  eyebrow,
  hasSolidBackground = false,
  hasTopDivider = true,
  headline,
  highlight,
  isHighlighted = false,
  link,
  linkVariant,
  mode = "light",
  sectionProps,
  text,
  theme = "white"
}: HeadlineBlockViewProps) {
  const cta = takeFirst(link);
  const icon = getPlainLinkIcon(cta);
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[{
    "color": "var(--text-primary)"
  }, theme === "grey" ? {
    "backgroundColor": "var(--bg-tertiary)"
  } : {
    "backgroundColor": "var(--bg-primary)"
  }, hasSolidBackground && {
    "position": "relative"
  }, hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }, highlight && {
    "backgroundColor": "var(--bg-primary) !important"
  }, theme === "grey" && {
    "backgroundColor": "var(--bg-tertiary)"
  }, isHighlighted && {
    "backgroundColor": "var(--white-static)"
  }]}>
      <_StyledWrap>
        <_StyledGrid $_css2={[{
        "gap": "var(--spacing-md)",
        "paddingLeft": "var(--spacing-xl)",
        "paddingRight": "var(--spacing-xl)",
        "paddingTop": "var(--spacing-xl)",
        "paddingBottom": "var(--spacing-xl)",
        "@media (min-width: 1024px)": {
          "paddingLeft": "var(--spacing-3xl)",
          "paddingRight": "var(--spacing-3xl)",
          "paddingTop": "var(--spacing-3xl)",
          "paddingBottom": "var(--spacing-3xl)"
        }
      }, !text && {
        "alignItems": "center"
      }]}>
          <_StyledDiv>
            {eyebrow && <Eyebrow text={eyebrow} mode={mode as Mode} />}
            {headline && <_StyledHeadline type="h2" text={headline} />}
          </_StyledDiv>
          {(text || isLinkValid(cta)) && <_StyledDiv2 $_css3={[{
          "position": "relative",
          "zIndex": "10",
          "gridColumn": "1 / -1",
          "display": "flex",
          "flexDirection": "column",
          "alignItems": "flex-start",
          "gap": "var(--spacing-lg)",
          "@media (min-width: 1024px)": {
            "gridColumn": "span 6 / span 6",
            "gap": "var(--spacing-xl)"
          }
        }, !text && {
          "@media (min-width: 1024px)": {
            "alignItems": "flex-end"
          }
        }]}>
              {text}
              {isLinkValid(cta) && <div>
                  <Button as={Link} {...cta} icon={icon} variant={linkVariant as ButtonVariant} />
                </div>}
            </_StyledDiv2>}
        </_StyledGrid>
      </_StyledWrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-1e4udgu-0"
})`${p => p.$_css}`;
var _StyledWrap = _styled2(Wrap).withConfig({
  componentId: "sc-sf-1e4udgu-1"
})({
  "borderWidth": "0px"
});
var _StyledGrid = _styled3(Grid).withConfig({
  componentId: "sc-sf-1e4udgu-2"
})`${p => p.$_css2}`;
var _StyledDiv = _styled4("div").withConfig({
  componentId: "sc-sf-1e4udgu-3"
})({
  "position": "relative",
  "zIndex": "10",
  "gridColumn": "1 / -1",
  "display": "flex",
  "flexDirection": "column",
  "gap": "var(--spacing-sm)",
  "@media (min-width: 1024px)": {
    "gridColumn": "span 6 / span 6"
  }
});
var _StyledHeadline = _styled5(Headline).withConfig({
  componentId: "sc-sf-1e4udgu-4"
})({
  "fontSize": "2.4rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.8rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.038rem",
    "fontWeight": "400"
  }
});
var _StyledDiv2 = /*#__PURE__*/_styled6("div").withConfig({
  displayName: "headline___StyledDiv2",
  componentId: "sc-1iynjfi-0"
})(["", ""], p => p.$_css3);